import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Button, ButtonSave } from 'components/buttons';
import { FormHeading, FormTextArea } from "components/form-fields";
import { ShowHide } from "components/layout/ShowHide";
import { NotificationInline, AlertConfirm } from 'components/notifications';
import { Intent } from "@blueprintjs/core";
import './Note.css';

export function Note(
    {
        setTitle = null,
        disabled = false,
        onSave = () => { },
        onDelete = () => { },
        notesValue = [],
        datesLabel = "",
        inModal = false,
        onNoteChange = null,
        isLoading
    }
) {

    const maxNoteSize = 1000;

    const [note, setNote] = useState("");
    const [showMultipleNotesWarning, setShowMultipleNotesWarning] = useState(false);
    const [deleteText, setDeleteText] = useState("Delete Note");
    const [canDelete, setCanDelete] = useState(false)
    const [deletingNote, setDeletingNote] = useState(false);
    const [deleteConfirmMessage, setDeleteConfirmMessage] = useState("");
    const inputTextArea = useRef(null);

    function onSaveNote() {
        onSave(note);
    }

    function onDeleteNote() {
        setDeletingNote(true);
    }

    function onDeleteValue() {
        setDeletingNote(false);
        onDelete();
        setNote("");
    }

    function onDeleteCancel() {
        setDeletingNote(false);
    }

    function onNoteChangeValue(event) {
        const newNote = event.target.value;
        setNote(newNote);
        if (onNoteChange) {
            onNoteChange(newNote);
        }
    }

    function focusOnInput() {
        if (inputTextArea.current != null) {
            inputTextArea.current.focus();
        }
    }

    useEffect(() => {
        let allNotesIdentical = true;
        let notesSet = false;
        let plural = notesValue.length > 1 ? "s" : "";

        if (notesValue && notesValue.length > 0 && typeof notesValue[0] === "string" && notesValue[0].length > 0) {
            setCanDelete(true);
        } else {
            setCanDelete(false);
        }

        if (!inModal) {
            if (notesValue.length > 0) {
                //Should only ever be 1 note value
                if (notesValue[0] !== note) {
                    //This is the original load
                    setNote(notesValue[0]);
                }
            }
        }
        else if (notesValue.length > 0) {
            notesValue.forEach((n) => {
                if (n !== notesValue[0]) {
                    allNotesIdentical = false;
                }
                if (n && typeof n === "string" && n.length > 0) {
                    notesSet = true;
                    setCanDelete(true);
                    setDeleteConfirmMessage(notesValue.length > 1 ? "Are you sure you want to delete these notes?" : "Are you sure you want to delete this note?");
                }
            });

            if (allNotesIdentical && notesSet) {
                setNote(notesValue[0]);
            } else {
                setNote("");
            }

            if (!allNotesIdentical && notesValue.length > 1) {
                setShowMultipleNotesWarning(true);
            }
        }

        if (setTitle) {
            setTitle(notesSet ? `Edit note${plural}` : `Add note${plural}`);
            setDeleteText(`Delete note${plural}`);
        }

        setTimeout(focusOnInput);
    }, [notesValue]);



    return (
        <>
            <ShowHide
                evaluator={inModal}
                hide={<FormHeading headingLevel="h3">Notes</FormHeading>}>
            </ShowHide>
            <NotificationInline allowClose={false} text={"The selected days contain different notes so are not shown. Saving will make the notes for the selected days identical."} intent="warning" show={showMultipleNotesWarning} />
            <FormHeading headingLevel="h4">{datesLabel}</FormHeading>
            <div className="note-container">
                <FormTextArea
                    id="asset-notes"
                    maxLength={maxNoteSize}
                    disabled={disabled}
                    loading={isLoading}
                    helperText="Please type your note here:"
                    value={note}
                    onChange={onNoteChangeValue}
                    inputRef={inputTextArea}
                    showCounter={true}
                ></FormTextArea>
            </div>
            <NotificationInline intent={Intent.WARNING} text="Note is too long, must be below 1000 characters" show={note.length > maxNoteSize} isShort />
            <div className="button-row button-row-stacked-mobile">
                <ButtonSave
                    onClick={onSaveNote}
                    disabled={disabled}
                    simpleDisabled={note.length < 1 || isLoading || deletingNote}
                />
                <ShowHide
                    evaluator={canDelete}
                    show={
                        <Button
                            onClick={onDeleteNote}
                            icon="trash"
                            text={deleteText}
                            disabled={disabled || !canDelete || isLoading || deletingNote}
                            intent='DANGER'
                            className="note-button-delete"
                        />
                    }
                >
                </ShowHide>
            </div>
            <AlertConfirm
                title={deleteText}
                isOpen={deletingNote}
                onConfirm={onDeleteValue}
                onCancel={onDeleteCancel}
                confirmButtonIntent="primary"
                confirmButtonText={deleteText}
            >
                <p>{deleteConfirmMessage}</p>
            </AlertConfirm>
        </>
    );
}

Note.propTypes = {
    setTitle: PropTypes.func,
    onSave: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    isLoading: PropTypes.bool,
    notesValue: PropTypes.array,
    datesLabel: PropTypes.string,
    inModal: PropTypes.bool,
    onNoteChange: PropTypes.func
};